<template>
  <div class="feature-requests-container">
    <div class="header">
      <h1>Feature Requests</h1>
    </div>

    <div class="actions-bar">
      <el-button
          type="primary"
          icon="el-icon-plus"
          @click="showAddModal = true">
        Add Feature Request
      </el-button>
    </div>

    <el-tabs
        v-model="activeTab"
        class="tabs"
        @tab-click="handleTabChange">
      <el-tab-pane
          label="Top Votes"
          name="votes">
        <div
            v-loading="loading"
            class="requests-list">
          <template v-if="featureRequests.length > 0">
            <feature-request-card
                v-for="request in featureRequests"
                :key="request.id"
                :request="request" />
          </template>
          <el-empty
              v-else
              description="No feature requests found" />
        </div>
      </el-tab-pane>

      <el-tab-pane
          label="Recent"
          name="recent">
        <div
            v-loading="loading"
            class="requests-list">
          <template v-if="featureRequests.length > 0">
            <feature-request-card
                v-for="request in featureRequests"
                :key="request.id"
                :request="request" />
          </template>
          <el-empty
              v-else
              description="No feature requests found" />
        </div>
      </el-tab-pane>
    </el-tabs>

    <div
        v-if="totalPages > 1"
        class="pagination">
      <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :total="totalItems"
          layout="prev, pager, next, total"
          @current-change="handlePageChange" />
    </div>

    <add-feature-request-modal
        :visible.sync="showAddModal"
        @created="handleFeatureRequestCreated" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import FeatureRequestCard from "@/components/FeatureRequests/FeatureRequestCard";
import AddFeatureRequestModal from "@/components/FeatureRequests/AddFeatureRequestModal";

const { mapGetters, mapActions } = createNamespacedHelpers("featureRequests");

export default {
  name: "FeatureRequests",
  components: {
    FeatureRequestCard,
    AddFeatureRequestModal,
  },
  data() {
    return {
      activeTab: "votes",
      showAddModal: false,
    };
  },
  computed: {
    ...mapGetters([
      "featureRequests",
      "loading",
      "currentPage",
      "pageSize",
      "totalPages",
      "totalItems",
      "sortOrder",
    ]),
  },
  mounted() {
    this.loadFeatureRequests();
  },
  methods: {
    ...mapActions(["fetchFeatureRequests", "setCurrentPage", "setSortOrder"]),
    loadFeatureRequests() {
      this.fetchFeatureRequests({
        page: this.currentPage,
        size: this.pageSize,
        sort: this.sortOrder,
      }).catch((error) => {
        this.$message({
          type: "error",
          message: "Failed to load feature requests. Please try again.",
          duration: 3000,
        });
      });
    },
    handleTabChange(tab) {
      const sortOrder = tab.name === "votes" ? "votes" : "recent";
      this.setSortOrder(sortOrder);
      this.setCurrentPage(1);
      this.loadFeatureRequests();
    },
    handlePageChange(page) {
      this.setCurrentPage(page);
      this.loadFeatureRequests();
      // Scroll to top of the page
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    handleFeatureRequestCreated() {
      // Reload the current page to show the new request
      this.loadFeatureRequests();
    },
  },
};
</script>

<style lang="scss" scoped>
.feature-requests-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px 30px;

  .header {
    margin-bottom: 30px;

    h1 {
      font-size: 32px;
      font-weight: 300;
      color: $--color-tundora;
      margin: 0 0 10px 0;
    }

    .subtitle {
      font-size: 16px;
      color: $--color-dusty-gray;
      margin: 0;
    }
  }

  .actions-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 25px;
  }

  .tabs {
    margin-bottom: 20px;

    ::v-deep .el-tabs__header {
      margin-bottom: 20px;
    }

    ::v-deep .el-tabs__item {
      font-size: 18px;
      padding: 0 30px;
      height: 50px;
      line-height: 50px;
    }

    ::v-deep .el-tabs__nav-wrap::after {
      background-color: #e4e4e4;
    }
  }

  .requests-list {
    min-height: 400px;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 30px;
    padding: 20px 0;

    ::v-deep .el-pagination {
      .el-pagination__total {
        color: $--color-dusty-gray;
      }

      .btn-prev,
      .btn-next {
        background: #fff;
        border: 1px solid #e4e4e4;

        &:hover {
          color: $--color-curious-blue;
        }
      }

      .el-pager li {
        background: #fff;
        border: 1px solid #e4e4e4;
        margin: 0 5px;

        &:hover {
          color: $--color-curious-blue;
        }

        &.active {
          background: $--color-curious-blue;
          color: #fff;
          border-color: $--color-curious-blue;
        }
      }
    }
  }
}
</style>

